import { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, message, Space, Table } from 'antd';
import { buildInQuery, IItemsWithCount, ILeadEntity, KeyValueRecord, LeadCreditStatusEnum } from '@rasayi-workspace/shared';
import { DEFAULT_TABLE_STATE } from '@constants';
import { ITableFilters, ITableState } from '@interfaces';
import { ColumnsType } from 'antd/es/table';
import { EMPTY_INITIAL_ITEMS, GetTableItems } from '@services';
import { TableProps } from 'antd/lib';
import { MapAntDesignSearchFilter, MapAntDesignSortOrder, RemoveUndefinedKeyPairs } from '@helpers';
import { SorterResult } from 'antd/es/table/interface';
import MoreIcon from 'src/assets/icons/moreIcon';
import { debounce, isBoolean } from 'lodash';
import { useLeadContext } from '@contexts';
import { leadsTableType } from '@types';
import { AddLeadButton, createColumns, getActionItems } from 'src/app/components/shared';
import { optionalColumnsType } from 'src/app/components/shared/types';
import { useMutation } from '@tanstack/react-query';
import { assignToCredit, holdLead } from 'src/app/services/lead';
import FilterBar from 'src/app/components/shared/filterBar';
import { HoldModal } from '../modals/holdModal';
import { ViewPlanModal } from '../modals/ViewPlanModal';

interface ICreditLeadsProps {
    activeTab: string;
    tabKey: string;
    actions: boolean;
    savedFilters: ITableFilters;
    setSavedFilters: (data: ITableFilters) => void;
    tableType: leadsTableType;
    setCount?: (count: string) => void;
    optionalColumns?: optionalColumnsType;
    optionalActions?: ('view' | 'approved' | 'rejected' | 'hold' | 'edit' | 'credit' | 'plan' | 'reasignCredit')[];
    isAddButton: boolean;
    getHoldDataOnInitialRender?: boolean;
}

export const LeadsTable = ({
    activeTab,
    tabKey,
    actions,
    setSavedFilters,
    savedFilters,
    tableType,
    setCount,
    optionalColumns,
    optionalActions,
    isAddButton,
    getHoldDataOnInitialRender,
}: ICreditLeadsProps) => {
    const { date, setHoldLeadsData } = useLeadContext();
    const [isPlanModalVisible, setIsPlanModalVisible] = useState('');
    const [holdModal, setHoldModal] = useState('');
    const [modalType, setModalType] = useState<'hold' | 'accept' | 'reject'>('hold');
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const [messageApi, contextHolder] = message.useMessage();
    const [leadModalVisible, setLeadModalVisible] = useState<boolean>(false);

    const [leadsData, setLeadsData] = useState<IItemsWithCount<ILeadEntity>>(EMPTY_INITIAL_ITEMS);

    const tableBasicFilters = {
        allLeads: `filter=credit.id,nl,_;drop_of,eq,false`,
        holdLeads: `filter=credit.id,nnl,true;credit.approvalStatus,eq,Pending;credit.holdApprovalStatus,eq,true;drop_of,eq,false`,
        dropOfLeads: `filter=drop_of,eq,true`,
        pendingCreditLeads: `filter=credit.id,nnl,true;credit.approvalStatus,eq,${LeadCreditStatusEnum.PENDING};credit.holdApprovalStatus,eq,false;drop_of,eq,false;is_repeated,eq,false`,
        repeatedCreditLeads: `filter=credit.id,nnl,true;credit.approvalStatus,eq,${LeadCreditStatusEnum.PENDING};credit.holdApprovalStatus,eq,false;drop_of,eq,false;is_repeated,eq,true`,
        approvedCreditLeads: `filter=credit.id,nnl,true;credit.approvalStatus,eq,${LeadCreditStatusEnum.APPROVED};credit.holdApprovalStatus,eq,false;drop_of,eq,false`,
        rejectedCreditLeads: `filter=credit.id,nnl,true;credit.approvalStatus,eq,${LeadCreditStatusEnum.REJECTED};credit.holdApprovalStatus,eq,false;drop_of,eq,false`,
    };

    const [tableState, setTableState] = useState<ITableState>({
        ...DEFAULT_TABLE_STATE,
        sortBy: { created_at: 'DESC' },
    });

    const [editingLeadId, setEditingLeadId] = useState('');

    const refetch = useCallback(
        async (tableStateParams: ITableState, searchParams?: string, qyeryFilter?: string, isFetcingHoldsData?: boolean) => {
            setIsFetching(true);
            try {
                let query = '';
                if (isFetcingHoldsData) query = `${tableBasicFilters['holdLeads']}`;
                else query = `${tableBasicFilters[tableType]}${searchParams ? `;${searchParams}` : ''}${qyeryFilter || ''}`;

                const returnedResult: IItemsWithCount<ILeadEntity> = await GetTableItems<ILeadEntity>(`lead?${query}`, ['product_type', 'channel', 'brand'], [], tableStateParams);

                if (isFetcingHoldsData) setHoldLeadsData(returnedResult);
                else {
                    if (tableType === 'holdLeads') setHoldLeadsData(returnedResult);
                    setLeadsData(returnedResult);
                    if (setCount) setCount(returnedResult.count.toString());
                }
            } catch (error) {
                if (!isFetcingHoldsData) {
                    setLeadsData(EMPTY_INITIAL_ITEMS);
                    if (setCount) setCount('');
                }
            } finally {
                setIsFetching(false);
            }
        },
        [tableType, date.startDate, date.endDate]
    );

    const { mutate: AssigneLeadToCreditHandler, isLoading: isUpdateLoading } = useMutation<any, unknown, { id: string; isHold?: boolean }>({
        mutationKey: ['updateLead'],
        mutationFn: async ({ id, isHold }) => {
            if (isBoolean(isHold)) return holdLead({ id, isHold: false });
            return assignToCredit(id);
        },
        onSuccess: (_data, variables) => {
            const { isHold } = variables;

            getData()(tableState, isHold);
            messageApi.success('Lead Assign To Credit successfully!');
        },
        onError: (e: any) => {
            messageApi.error(
                <div>
                    {e?.response?.data?.message?.map((msg: string, index: number) => (
                        <div className="text-start	" key={index}>
                            {msg}
                        </div>
                    )) || 'Failed to update lead. Contact support.'}
                </div>
            );
        },
    });

    const { mutate: MoveLeadFromHoldHandler, isLoading: isUpdateHoldLoading } = useMutation<any, unknown, { id: string }>({
        mutationKey: ['moveHoldLead'],
        mutationFn: async ({ id }) => {
            return holdLead({ id, isHold: false });
        },
        onSuccess: (_data, variables) => {
            getData()(tableState);
            messageApi.success('Lead Assign To Credit successfully!');
        },
        onError: (e: any) => {
            messageApi.error(
                <div>
                    {e?.response?.data?.message?.map((msg: string, index: number) => (
                        <div className="text-start	" key={index}>
                            {msg}
                        </div>
                    )) || 'Failed to update lead. Contact support.'}
                </div>
            );
        },
    });

    const handleChange: TableProps<ILeadEntity>['onChange'] = (pagination, filters, sorter) => {
        let sortBy: KeyValueRecord = RemoveUndefinedKeyPairs({
            [(sorter as SorterResult<ILeadEntity>).columnKey as keyof ILeadEntity]: MapAntDesignSortOrder((sorter as SorterResult<ILeadEntity>).order),
        });

        sortBy = Object.keys(sortBy).length ? sortBy : DEFAULT_TABLE_STATE.sortBy;

        setTableState({
            ...tableState,
            page: pagination.current || tableState.page,
            pageSize: pagination.pageSize || tableState.pageSize,
            sortBy: sortBy,
            search: MapAntDesignSearchFilter(RemoveUndefinedKeyPairs(filters)),
        });
    };

    const getColumns = (actions: boolean, optionalColumns?: optionalColumnsType): ColumnsType<ILeadEntity> => {
        let columns: ColumnsType<ILeadEntity> = createColumns(optionalColumns);

        if (actions) {
            columns.push({
                title: 'Actions',
                dataIndex: 'operation',
                key: 'operation',
                align: 'right',
                fixed: 'right',
                render: (_: string, item: ILeadEntity) => (
                    <div className="w-[60px] flex justify-end">
                        <ActionModal item={item}>
                            <Button type="text" shape="circle" icon={<MoreIcon />} className="!text-end h-[40px] w-[20px]" />
                        </ActionModal>
                    </div>
                ),
            });
        }

        return columns;
    };

    const ActionModal: React.FC<{ children: React.ReactNode; item: ILeadEntity }> = ({ children, item }) => {
        return (
            <Dropdown
                menu={{
                    items: getActionItems({
                        item,
                        optionalActions,
                        setHoldModal,
                        setModalType,
                        setLeadModalVisible,
                        setEditingLeadId,
                        isUpdateLoading: isUpdateHoldLoading || isUpdateHoldLoading,
                        AssigneLeadToCreditHandler,
                        setIsPlanModalVisible,
                        MoveLeadFromHoldHandler,
                    }),
                }}
                trigger={['click']}
                placement="topLeft"
            >
                <a onClick={e => e.preventDefault()}>
                    <Space wrap direction="vertical">
                        {children}
                    </Space>
                </a>
            </Dropdown>
        );
    };

    const getData = useCallback(() => {
        return debounce((tableStateParams: ITableState, isFetcingHoldsData?: boolean) => {
            const filters = { ...savedFilters };
            let queryParts: string[] = [];

            queryParts.push(buildInQuery('user_id', filters.agent));
            queryParts.push(buildInQuery('lead_status', filters.status));
            queryParts.push(buildInQuery('products.brand_id', filters.brand));
            queryParts.push(buildInQuery('channel_id', filters.channel));
            queryParts.push(buildInQuery('city', filters.city));
            queryParts.push(buildInQuery('segment', filters.segment));
            queryParts.push(buildInQuery('products.product_type_id', filters.productType));

            if (filters.quantity !== null && filters.quantity !== undefined) {
                queryParts.push(`products.quantity,eq,${filters.quantity}:`);
            }
            // Date filters
            if (filters?.createdAt) {
                queryParts.push(`createdAt,ge,${filters.createdAt}`);
            }

            // Join parts and trim extra colons
            let query = queryParts.join('').replace(/;+$/, '');

            let manualFilter = '';
            if (savedFilters.searchedText) manualFilter += `&search_text=${savedFilters.searchedText}`;
            if (date?.startDate && date?.endDate) manualFilter += `&startDate=${date.startDate}&endDate=${date.endDate}`;

            refetch(tableStateParams, query, manualFilter, isFetcingHoldsData);
        }, 1000);
    }, [savedFilters, date?.startDate, date?.endDate]);

    useEffect(() => {
        if (tabKey === activeTab) {
            getData()(tableState);
        }
    }, [activeTab, getData, tableState.page, tableState.search, tableState.sortBy]);

    useEffect(() => {
        if (getHoldDataOnInitialRender) {
            getData()(DEFAULT_TABLE_STATE, true);
        }
    }, [getHoldDataOnInitialRender]);

    return (
        <>
            {contextHolder}
            <FilterBar
                showSearch={true}
                showFilterButton={true}
                showAddButton={isAddButton}
                onSearch={() => {}}
                onFilterClick={() => {}}
                setLeadModalVisible={setLeadModalVisible}
                leadModalVisible={leadModalVisible}
                onRemoveFilter={() => {}}
                refetchWithFilters={setSavedFilters}
                AddButton={
                    <AddLeadButton
                        leadId={editingLeadId}
                        leadModalVisible={leadModalVisible}
                        setLeadModalVisible={setLeadModalVisible}
                        refetch={() => {
                            getData()(DEFAULT_TABLE_STATE);
                        }}
                        setLeadId={setEditingLeadId}
                    />
                }
                filtersToShow={['agnet', 'brand', 'channel', 'city', 'created_at', 'lead_status', 'product_type', 'quantity', 'search_text', 'segment']}
            />

          <div className="border-2 border-black rounded-lg">
            <Table
              columns={getColumns(actions, optionalColumns)}
              dataSource={leadsData.items}
              onChange={handleChange}
              pagination={{
                total: leadsData.count,
                pageSize: leadsData.pageSize,
                current: leadsData.page,
                size: 'default',
                className: 'ant-pagination-centered',
              }}
              loading={isFetching}
              rowKey={'id'}
              scroll={{ x: true }}
            />
          </div>
            {isPlanModalVisible ? (
                <ViewPlanModal
                    visible={isPlanModalVisible}
                    onClose={() => {
                        setIsPlanModalVisible('');
                    }}
                />
            ) : null}
            {holdModal ? (
                <HoldModal
                    modalType={modalType}
                    visible={holdModal}
                    onClose={() => {
                        setHoldModal('');
                    }}
                    refetch={() => {
                        getData()(DEFAULT_TABLE_STATE);
                    }}
                />
            ) : null}
        </>
    );
};
